export type DashboardJobPhotoRoutes = {
  dashboardJobPhotosUploadUrl?: string;
  dashboardJobPhotosConfirm?: string;
};

export function dashboardJobPhotosUploadHref(
  routes: DashboardJobPhotoRoutes,
  jobId: string,
): string {
  return (
    routes.dashboardJobPhotosUploadUrl ??
    "/dashboard/jobs/:job_id/photos/upload-url"
  ).replace(":job_id", jobId);
}

export function dashboardJobPhotosConfirmHref(
  routes: DashboardJobPhotoRoutes,
  jobId: string,
): string {
  return (
    routes.dashboardJobPhotosConfirm ??
    "/dashboard/jobs/:job_id/photos/bulk-confirm"
  ).replace(":job_id", jobId);
}

type UploadUrlResponse = {
  uploadUrl: string;
  storageKey: string;
  mediaType?: string;
  error?: string;
};

export async function uploadJobPhotoFile(options: {
  file: File;
  jobId: string;
  csrfToken: string;
  routes: DashboardJobPhotoRoutes;
}): Promise<void> {
  const { file, jobId, csrfToken, routes } = options;
  const contentType = file.type.trim() || "application/octet-stream";
  const uploadPath = dashboardJobPhotosUploadHref(routes, jobId);
  const confirmPath = dashboardJobPhotosConfirmHref(routes, jobId);

  const uploadRes = await fetch(uploadPath, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken,
    },
    body: JSON.stringify({ contentType, filename: file.name }),
  });

  const uploadPayload = (await uploadRes
    .json()
    .catch(() => ({}))) as UploadUrlResponse;
  if (!uploadRes.ok || !uploadPayload.uploadUrl || !uploadPayload.storageKey) {
    throw new Error(uploadPayload.error || "Could not start the upload.");
  }

  const putRes = await fetch(uploadPayload.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: file,
  });
  if (!putRes.ok) {
    const text = await putRes.text().catch(() => "");
    throw new Error(`Upload failed (${putRes.status}): ${text}`);
  }

  const confirmRes = await fetch(confirmPath, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken,
    },
    body: JSON.stringify({
      items: [
        {
          storageKey: uploadPayload.storageKey,
          mediaType: uploadPayload.mediaType,
        },
      ],
    }),
  });

  if (!confirmRes.ok) {
    const confirmPayload = (await confirmRes.json().catch(() => ({}))) as {
      error?: string;
    };
    throw new Error(confirmPayload.error || "Could not save the upload.");
  }
}
